import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';
import type { Readable } from 'stream';

// Disable default body parser for multipart/form-data
export const config = {
  api: {
    bodyParser: false,
  },
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ALLOWED_EXTENSIONS = new Set(['.pdf', '.jpg', '.jpeg', '.png', '.xlsx', '.csv', '.xml']);
const STORAGE_BUCKET = 'documentos';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const MIME_MAP: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.csv': 'text/csv',
  '.xml': 'application/xml',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function readRawBody(req: NextApiRequest): Promise<Buffer> {
  const chunks: Buffer[] = [];
  for await (const chunk of req as unknown as Readable) {
    chunks.push(typeof chunk === 'string' ? Buffer.from(chunk) : chunk);
  }
  return Buffer.concat(chunks);
}

interface ParsedMultipart {
  fields: Record<string, string>;
  file?: { filename: string; content: Buffer; contentType: string };
}

async function parseMultipart(req: NextApiRequest): Promise<ParsedMultipart> {
  const contentType = req.headers['content-type'] || '';
  const boundaryMatch = contentType.match(/boundary=(.+)/);
  if (!boundaryMatch) {
    throw new Error('No se encontró boundary en el Content-Type multipart.');
  }

  const boundary = boundaryMatch[1];
  const rawBody = await readRawBody(req);
  const bodyStr = rawBody.toString('latin1');
  const parts = bodyStr.split(`--${boundary}`).filter(
    (p) => p.trim() !== '' && p.trim() !== '--'
  );

  const result: ParsedMultipart = { fields: {} };

  for (const part of parts) {
    const headerEnd = part.indexOf('\r\n\r\n');
    if (headerEnd === -1) continue;

    const headerSection = part.substring(0, headerEnd);
    const bodySection = part.substring(headerEnd + 4).replace(/\r\n$/, '');

    const nameMatch = headerSection.match(/name="([^"]+)"/);
    if (!nameMatch) continue;
    const fieldName = nameMatch[1];

    const filenameMatch = headerSection.match(/filename="([^"]+)"/);
    if (filenameMatch) {
      const contentTypeMatch = headerSection.match(/Content-Type:\s*(.+)/i);
      const start = rawBody.indexOf(
        Buffer.from('\r\n\r\n', 'latin1'),
        rawBody.indexOf(Buffer.from(headerSection.substring(0, 40), 'latin1'))
      ) + 4;
      const endMarker = Buffer.from(`\r\n--${boundary}`, 'latin1');
      const end = rawBody.indexOf(endMarker, start);
      result.file = {
        filename: filenameMatch[1],
        content: rawBody.subarray(start, end !== -1 ? end : undefined),
        contentType: contentTypeMatch ? contentTypeMatch[1].trim() : 'application/octet-stream',
      };
    } else {
      result.fields[fieldName] = bodySection;
    }
  }

  return result;
}

function getExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf('.');
  return dotIndex >= 0 ? filename.substring(dotIndex).toLowerCase() : '';
}

function sanitizeFilename(filename: string): string {
  return filename.replace(/[^\w.\-]/g, '_');
}

// ─── Handler ─────────────────────────────────────────────────────────────────

/**
 * POST /api/assistant/upload-file
 *
 * Accepts multipart/form-data with a single file for the AI assistant chat.
 * Uploads to Supabase Storage and creates a record in the `documentos` table.
 *
 * Form fields:
 *   - file: The file to upload (required)
 *   - asesorId: string (required)
 *   - clienteId: string (optional)
 *
 * Returns: { success, documentId, publicUrl, nombre }
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ success: false, error: 'Method not allowed' });
  }

  const supabaseAdmin = getSupabaseAdminClient();

  try {
    // Parse multipart form data
    const formData = await parseMultipart(req);

    const asesorId = formData.fields['asesorId'];
    const clienteId = formData.fields['clienteId'] || null;

    if (!asesorId) {
      return res.status(400).json({ success: false, error: 'asesorId es requerido.' });
    }

    if (!formData.file) {
      return res.status(400).json({ success: false, error: 'No se proporcionó archivo.' });
    }

    const { filename, content, contentType } = formData.file;

    // Validate file size
    if (content.length > MAX_FILE_SIZE) {
      return res.status(400).json({
        success: false,
        error: `El archivo excede el tamaño máximo de ${MAX_FILE_SIZE / (1024 * 1024)}MB.`,
      });
    }

    // Validate file extension
    const ext = getExtension(filename);
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      return res.status(400).json({
        success: false,
        error: `Tipo de archivo no permitido. Extensiones válidas: ${[...ALLOWED_EXTENSIONS].join(', ')}`,
      });
    }

    // Build storage path: assistant/{asesorId}/{timestamp}_{sanitizedFilename}
    const timestamp = Date.now();
    const safeName = sanitizeFilename(filename);
    const storagePath = `assistant/${asesorId}/${timestamp}_${safeName}`;
    const mimeType = MIME_MAP[ext] || contentType || 'application/octet-stream';

    // Upload to Supabase Storage
    const { error: uploadError } = await supabaseAdmin.storage
      .from(STORAGE_BUCKET)
      .upload(storagePath, content, {
        contentType: mimeType,
        upsert: false,
      });

    if (uploadError) {
      console.error('[upload-file] Storage upload error:', uploadError);
      return res.status(500).json({
        success: false,
        error: `Error subiendo archivo: ${uploadError.message}`,
      });
    }

    // Get public URL
    const { data: urlData } = supabaseAdmin.storage
      .from(STORAGE_BUCKET)
      .getPublicUrl(storagePath);
    const publicUrl = urlData?.publicUrl ?? '';

    // Create document record in 'documentos' table
    const { data: insertData, error: insertError } = await supabaseAdmin
      .from('documentos')
      .insert({
        nombre: filename,
        cliente_id: clienteId,
        tipo: 'otro',
        estado: 'pendiente',
        url_archivo: publicUrl,
        tamaño: content.length,
      })
      .select('id')
      .single();

    if (insertError) {
      console.error('[upload-file] DB insert error:', insertError);
      return res.status(500).json({
        success: false,
        error: `Error registrando documento: ${insertError.message}`,
      });
    }

    return res.status(200).json({
      success: true,
      documentId: insertData.id,
      publicUrl,
      nombre: filename,
    });
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : 'Error desconocido';
    console.error('[upload-file] Error:', msg);
    return res.status(500).json({
      success: false,
      error: msg,
    });
  }
}

export default withAuth(handler, ['admin', 'asesor']);
